/**
 * Find "pleasant" waypoints between start and end so Mapbox actually
 * routes us along different roads (water, parks, historic, landmarks),
 * instead of just re-ranking the same shortest-path alternatives.
 *
 * Strategy:
 *   1. Build a corridor bbox around the straight line.
 *   2. Query Overpass for parks/water/historic/landmarks in that corridor
 *      based on the user's `prefs` AND their `pleasantnessProfile`. The
 *      profile is a hard backstop so we always have *something* to detour
 *      through, even when the user picked no specific loves.
 *   3. Score each candidate by perpendicular deviation from the direct
 *      line, centrality along the trip, and feature kind weight.
 *   4. Return the TOP-N candidates spread out along the corridor, so the
 *      caller can fetch genuinely-different route geometries through each.
 */
import type { LngLat } from "@/utils/polyline";
import type { Preference } from "@/utils/scoring";
import type { TravelMode } from "@/api/routing";
import type { PleasantnessProfile } from "@/utils/profile";
import { fetchTagsInBbox, type CommunityTag } from "@/api/community";
import { overpassDirect } from "@/api/osm";

/** Anchor kinds the wider-net search treats as "pleasant."
 *  - water/park/historic/landmark come from OSM (Part 7 of PLEASANT_RULES.md).
 *  - "community" comes from clusters of positive community ratings (#98) —
 *    a spot is community-anchored when ≥3 users rated it positively (≥2 stars)
 *    within ~50m. Treats locally-loved places as routable anchors, not just
 *    passive scoring boosts. */
export type WaypointKind = "water" | "park" | "historic" | "landmark" | "community";

/** Min positive community tags within ~50m to form a community anchor. */
export const COMMUNITY_ANCHOR_MIN_TAGS = 3;
/** Clustering radius (≈50m). */
const COMMUNITY_ANCHOR_GRID_DEG = 0.00045;

export interface PleasantWaypoint {
  coord: LngLat;
  kind: WaypointKind;
  name?: string;
  /** Internal score 0..~1.6, higher is better. Exposed mainly for debugging. */
  score?: number;
  /** Position along the trip, 0 = at start, 1 = at end. Used by the
   *  multi-waypoint sequence route in generateRoutes so it can thread
   *  picks in trip order. Optional because legacy seeds (the curated
   *  street catalog, hardcoded localBackroad waypoints) don't carry it. */
  alongT?: number;
}

interface Candidate extends PleasantWaypoint {
  /** Distance along trip (0 = at start, 1 = at end). */
  alongT: number;
}

/** Haversine km. */
function distKm(a: LngLat, b: LngLat): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

/** Perpendicular-ish deviation of point p from segment a->b, in km (rough). */
function projectOntoSegment(
  a: LngLat,
  b: LngLat,
  p: LngLat,
): { devKm: number; t: number } {
  const toXY = (ll: LngLat) =>
    [ll[0] * Math.cos((a[1] * Math.PI) / 180), ll[1]] as const;
  const [ax, ay] = toXY(a);
  const [bx, by] = toXY(b);
  const [px, py] = toXY(p);
  const dx = bx - ax,
    dy = by - ay;
  const len2 = dx * dx + dy * dy || 1e-9;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  const cx = ax + t * dx,
    cy = ay + t * dy;
  const dLon = px - cx,
    dLat = py - cy;
  return { devKm: Math.sqrt(dLon * dLon + dLat * dLat) * 111, t };
}

function corridorBbox(start: LngLat, end: LngLat, padDeg: number) {
  const minLat = Math.min(start[1], end[1]) - padDeg;
  const maxLat = Math.max(start[1], end[1]) + padDeg;
  const minLng = Math.min(start[0], end[0]) - padDeg;
  const maxLng = Math.max(start[0], end[0]) + padDeg;
  return { minLat, maxLat, minLng, maxLng };
}

function insideBox(p: LngLat, box: { minLng: number; maxLng: number; minLat: number; maxLat: number }) {
  return p[0] >= box.minLng && p[0] <= box.maxLng && p[1] >= box.minLat && p[1] <= box.maxLat;
}

function localBackroadWaypoints(start: LngLat, end: LngLat, mode?: TravelMode): PleasantWaypoint[] {
  const seq = localBackroadSequence(start, end, mode);
  return seq?.waypoints ?? [];
}

/**
 * Curated catalog of famously beautiful walking streets / promenades in
 * major cities. When a walking trip's straight-line corridor passes near
 * one of these, we inject it as a high-score candidate so Mapbox routes
 * the walk through it. These are streets locals universally agree are
 * gorgeous on foot — tree-lined, brownstones, pedestrian malls, waterfront
 * promenades — that the generic "park / water polygon centroid" search
 * would never surface as a routable waypoint.
 *
 * Tuning: `coord` should sit ON the street mid-block so Mapbox snaps
 * cleanly. Keep the list short and high-signal — bad picks force detours
 * for nothing.
 */
export interface BeautifulWalkingStreet { coord: LngLat; name: string; }
export const BEAUTIFUL_WALKING_STREETS: BeautifulWalkingStreet[] = [
  // New York City — parks & promenades
  { coord: [-73.9709, 40.7745], name: "Central Park Mall" },
  { coord: [-73.9683, 40.7794], name: "Bethesda Terrace (Central Park)" },
  { coord: [-74.0064, 40.7053], name: "Brooklyn Bridge promenade" },
  { coord: [-73.9962, 40.6975], name: "Brooklyn Heights Promenade" },
  { coord: [-74.0094, 40.7424], name: "The High Line" },
  { coord: [-74.0044, 40.7307], name: "Washington Square Park" },
  { coord: [-73.9819, 40.7829], name: "Fifth Avenue Museum Mile" },
  { coord: [-73.9876, 40.7616], name: "Fifth Ave at Central Park" },
  { coord: [-73.9626, 40.6764], name: "Prospect Park West" },
  { coord: [-73.9911, 40.7505], name: "Bryant Park" },
  // New York City — iconic walking neighborhoods (mid-block coords so
  // Mapbox snaps onto the charming street, not the nearest arterial)
  { coord: [-73.9998, 40.7358], name: "West Village (Bleecker St)" },
  { coord: [-74.0038, 40.7338], name: "West Village (Hudson St)" },
  { coord: [-74.0026, 40.7320], name: "West Village (Bedford & Grove)" },
  { coord: [-73.9978, 40.7312], name: "Greenwich Village (MacDougal St)" },
  { coord: [-73.9962, 40.7301], name: "Greenwich Village (Sullivan St)" },
  { coord: [-73.9985, 40.7250], name: "SoHo (Prince St)" },
  { coord: [-73.9997, 40.7237], name: "SoHo (Greene St cobblestones)" },
  { coord: [-74.0024, 40.7228], name: "SoHo (Spring & Sullivan)" },
  { coord: [-74.0070, 40.7195], name: "Tribeca (Hudson & Franklin)" },
  { coord: [-74.0093, 40.7166], name: "Tribeca (Greenwich St)" },
  { coord: [-73.9911, 40.7359], name: "Union Square Park" },
  { coord: [-73.9904, 40.7414], name: "Madison Square Park" },
  { coord: [-73.9879, 40.7393], name: "Gramercy Park edge" },
  { coord: [-74.0094, 40.7280], name: "Hudson River Greenway (West Village)" },
  { coord: [-74.0150, 40.7200], name: "Hudson River Greenway (Tribeca)" },

  // San Francisco
  { coord: [-122.4783, 37.8199], name: "Crissy Field promenade" },
  { coord: [-122.4477, 37.7693], name: "Golden Gate Park (JFK Dr)" },
  { coord: [-122.4089, 37.7984], name: "Embarcadero waterfront" },
  { coord: [-122.4098, 37.7956], name: "Jackson Square (oldest brick blocks)" },
  { coord: [-122.4078, 37.7946], name: "North Beach (Grant Ave)" },
  { coord: [-122.4194, 37.7595], name: "Mission (Valencia St)" },
  { coord: [-122.4078, 37.7517], name: "Bernal Heights (Cortland Ave)" },
  // Los Angeles
  { coord: [-118.2479, 34.0552], name: "Olvera Street (oldest LA block)" },
  { coord: [-118.4946, 34.0099], name: "Venice Boardwalk" },
  { coord: [-118.4951, 34.0028], name: "Abbot Kinney Blvd (Venice)" },
  { coord: [-118.4691, 34.0094], name: "Santa Monica Pier promenade" },
  { coord: [-118.2972, 34.1019], name: "Hollywood Walk of Fame" },
  // Seattle
  { coord: [-122.3422, 47.6088], name: "Pike Place Market" },
  { coord: [-122.3499, 47.6093], name: "Seattle waterfront promenade" },
  // Boston
  { coord: [-71.0759, 42.3505], name: "Commonwealth Ave Mall" },
  { coord: [-71.0704, 42.3551], name: "Boston Public Garden" },
  { coord: [-71.0731, 42.3588], name: "Beacon Hill (Charles St)" },
  { coord: [-71.0775, 42.3588], name: "Acorn Street (cobblestone)" },
  { coord: [-71.0563, 42.3650], name: "North End (Hanover St)" },
  // Washington DC
  { coord: [-77.0214, 38.8895], name: "National Mall" },
  { coord: [-77.0617, 38.9051], name: "Georgetown (M Street)" },
  { coord: [-77.0641, 38.9050], name: "Georgetown Waterfront" },
  // Chicago
  { coord: [-87.6224, 41.8826], name: "Chicago Riverwalk" },
  { coord: [-87.6166, 41.8826], name: "Millennium Park" },
  { coord: [-87.6196, 41.8917], name: "Magnificent Mile" },
  { coord: [-87.6230, 41.8917], name: "Old Town (Wells St)" },
  // Philadelphia
  { coord: [-75.1448, 39.9523], name: "Elfreth's Alley" },
  { coord: [-75.1503, 39.9489], name: "Independence Mall" },
  { coord: [-75.1715, 39.9656], name: "Rittenhouse Square" },
  // New Orleans
  { coord: [-90.0648, 29.9584], name: "French Quarter (Royal St)" },
  { coord: [-90.0710, 29.9270], name: "Garden District (Magazine St)" },
  // Charleston
  { coord: [-79.9285, 32.7717], name: "Rainbow Row" },
  { coord: [-79.9320, 32.7702], name: "The Battery promenade" },
  // Savannah
  { coord: [-81.0912, 32.0809], name: "Forsyth Park (oak canopy)" },
  { coord: [-81.0911, 32.0809], name: "Bull St (squares)" },
  // Miami
  { coord: [-80.1300, 25.7806], name: "Ocean Drive (Art Deco)" },
  { coord: [-80.2540, 25.7444], name: "Coral Gables (Miracle Mile)" },
  // Toronto
  { coord: [-79.3781, 43.6500], name: "Distillery District" },
  { coord: [-79.4150, 43.6480], name: "Kensington Market" },
  // Montreal
  { coord: [-73.5564, 45.5076], name: "Old Montreal (Rue Saint-Paul)" },
  { coord: [-73.5849, 45.5183], name: "Mile End (Saint-Viateur)" },
  // Vancouver
  { coord: [-123.1288, 49.3000], name: "Stanley Park Seawall" },
  { coord: [-123.1042, 49.2832], name: "Gastown (Water St)" },
  // Mexico City
  { coord: [-99.1672, 19.4127], name: "Roma Norte (Álvaro Obregón)" },
  { coord: [-99.1715, 19.4112], name: "Condesa (Av. Amsterdam loop)" },
  { coord: [-99.1810, 19.3559], name: "Coyoacán (Plaza Hidalgo)" },
  // Paris
  { coord: [2.3076, 48.8716], name: "Champs-Élysées" },
  { coord: [2.3499, 48.8530], name: "Île Saint-Louis" },
  { coord: [2.3266, 48.8649], name: "Tuileries Garden" },
  { coord: [2.3349, 48.8554], name: "Seine quays (Pont des Arts)" },
  { coord: [2.3477, 48.8590], name: "Le Marais (Rue des Rosiers)" },
  { coord: [2.3415, 48.8848], name: "Montmartre (Rue Lepic)" },
  { coord: [2.3308, 48.8499], name: "Saint-Germain (Rue de Buci)" },
  { coord: [2.3593, 48.8729], name: "Canal Saint-Martin" },
  // London
  { coord: [-0.1276, 51.5074], name: "Trafalgar Square / The Mall" },
  { coord: [-0.1657, 51.5073], name: "Hyde Park (Serpentine)" },
  { coord: [-0.1037, 51.5145], name: "South Bank promenade" },
  { coord: [-0.1411, 51.5118], name: "Mayfair (Mount St)" },
  { coord: [-0.1759, 51.4965], name: "Notting Hill (Portobello Rd)" },
  { coord: [-0.1234, 51.5145], name: "Soho (Old Compton St)" },
  { coord: [-0.1738, 51.5095], name: "Kensington (Kensington Church St)" },
  { coord: [-0.0876, 51.5246], name: "Shoreditch (Redchurch St)" },
  // Edinburgh
  { coord: [-3.1900, 55.9494], name: "Royal Mile" },
  { coord: [-3.1956, 55.9533], name: "Dean Village (Water of Leith)" },
  // Dublin
  { coord: [-6.2602, 53.3441], name: "Temple Bar" },
  { coord: [-6.2674, 53.3382], name: "Grafton Street" },
  // Amsterdam
  { coord: [4.8836, 52.3677], name: "Jordaan canals (Prinsengracht)" },
  { coord: [4.8847, 52.3690], name: "Negen Straatjes (Nine Streets)" },
  { coord: [4.8995, 52.3613], name: "Vondelpark" },
  // Brussels
  { coord: [4.3528, 50.8467], name: "Grand-Place" },
  { coord: [4.3500, 50.8420], name: "Sablon (antiques quarter)" },
  // Berlin
  { coord: [13.4050, 52.5200], name: "Unter den Linden" },
  { coord: [13.4254, 52.5316], name: "Prenzlauer Berg (Kollwitzplatz)" },
  { coord: [13.4181, 52.4995], name: "Kreuzberg (Bergmannstraße)" },
  // Munich
  { coord: [11.5760, 48.1391], name: "Marienplatz" },
  { coord: [11.5950, 48.1532], name: "English Garden" },
  // Vienna
  { coord: [16.3738, 48.2082], name: "Innere Stadt (Graben)" },
  { coord: [16.3527, 48.2105], name: "Ringstrasse promenade" },
  // Prague
  { coord: [14.4145, 50.0875], name: "Old Town Square" },
  { coord: [14.4115, 50.0865], name: "Charles Bridge" },
  { coord: [14.4035, 50.0898], name: "Malá Strana (Nerudova)" },
  // Budapest
  { coord: [19.0398, 47.5006], name: "Castle District (Úri utca)" },
  { coord: [19.0540, 47.4979], name: "Andrássy Avenue" },
  // Krakow
  { coord: [19.9370, 50.0617], name: "Rynek Główny (Main Square)" },
  { coord: [19.9450, 50.0517], name: "Kazimierz (Szeroka)" },
  // Copenhagen
  { coord: [12.5805, 55.6797], name: "Nyhavn waterfront" },
  { coord: [12.5703, 55.6814], name: "Strøget pedestrian street" },
  // Stockholm
  { coord: [18.0710, 59.3257], name: "Gamla Stan (Västerlånggatan)" },
  { coord: [18.0860, 59.3293], name: "Djurgården promenade" },
  // Oslo
  { coord: [10.7522, 59.9099], name: "Aker Brygge waterfront" },
  // Reykjavik
  { coord: [-21.9426, 64.1466], name: "Laugavegur" },
  // Madrid
  { coord: [-3.7038, 40.4168], name: "Plaza Mayor" },
  { coord: [-3.6936, 40.4239], name: "Malasaña (Calle Fuencarral)" },
  { coord: [-3.6920, 40.4131], name: "Lavapiés / La Latina" },
  { coord: [-3.6845, 40.4153], name: "Retiro Park" },
  // Barcelona
  { coord: [2.1734, 41.3818], name: "La Rambla" },
  { coord: [2.1734, 41.3954], name: "Passeig de Gràcia" },
  { coord: [2.1812, 41.3833], name: "El Born (Carrer Montcada)" },
  { coord: [2.1574, 41.3995], name: "Gràcia (Plaça del Sol)" },
  { coord: [2.1899, 41.3789], name: "Barceloneta beach promenade" },
  // Seville
  { coord: [-5.9926, 37.3826], name: "Barrio Santa Cruz" },
  { coord: [-5.9869, 37.3753], name: "Plaza de España" },
  // Lisbon
  { coord: [-9.1393, 38.7138], name: "Alfama (Rua de São Pedro)" },
  { coord: [-9.1454, 38.7106], name: "Bairro Alto" },
  { coord: [-9.1604, 38.6979], name: "Belém (waterfront)" },
  // Porto
  { coord: [-8.6109, 41.1408], name: "Ribeira (Cais da Ribeira)" },
  // Rome
  { coord: [12.4823, 41.8902], name: "Trastevere" },
  { coord: [12.4769, 41.9009], name: "Centro Storico (Via dei Coronari)" },
  { coord: [12.4823, 41.9059], name: "Piazza Navona" },
  { coord: [12.4534, 41.9098], name: "Monti (Via del Boschetto)" },
  // Florence
  { coord: [11.2558, 43.7696], name: "Oltrarno (Via Maggio)" },
  { coord: [11.2530, 43.7681], name: "Ponte Vecchio" },
  // Venice
  { coord: [12.3155, 45.4408], name: "Cannaregio (Fondamenta Misericordia)" },
  { coord: [12.3358, 45.4340], name: "Piazza San Marco" },
  // Milan
  { coord: [9.1900, 45.4654], name: "Brera (Via Brera)" },
  { coord: [9.1721, 45.4506], name: "Navigli (Naviglio Grande)" },
  // Naples
  { coord: [14.2545, 40.8505], name: "Spaccanapoli" },
  // Athens
  { coord: [23.7287, 37.9755], name: "Plaka (Adrianou St)" },
  { coord: [23.7230, 37.9772], name: "Anafiotika (Cycladic stairs)" },
  // Istanbul
  { coord: [28.9784, 41.0258], name: "Sultanahmet" },
  { coord: [28.9774, 41.0317], name: "Galata (Serdar-ı Ekrem)" },
  { coord: [28.9870, 41.0392], name: "İstiklal Caddesi" },
  // Marrakech
  { coord: [-7.9883, 31.6258], name: "Medina souks (Jemaa el-Fnaa)" },
  // Cairo
  { coord: [31.2627, 30.0479], name: "Khan el-Khalili" },
  // Cape Town
  { coord: [18.4233, -33.9249], name: "Bo-Kaap (Wale St)" },
  { coord: [18.4181, -33.9028], name: "V&A Waterfront" },
  // Tokyo
  { coord: [139.7035, 35.6620], name: "Omotesandō" },
  { coord: [139.7036, 35.7148], name: "Yanaka (Yanaka Ginza)" },
  { coord: [139.7706, 35.7148], name: "Asakusa (Nakamise-dōri)" },
  { coord: [139.7016, 35.6643], name: "Shibuya / Cat St" },
  { coord: [139.7677, 35.7228], name: "Kagurazaka" },
  // Kyoto
  { coord: [135.7836, 35.0036], name: "Gion (Hanamikōji)" },
  { coord: [135.6711, 35.0170], name: "Arashiyama bamboo grove" },
  { coord: [135.7817, 35.0078], name: "Philosopher's Path" },
  { coord: [135.7710, 34.9980], name: "Pontocho Alley" },
  // Osaka
  { coord: [135.5023, 34.6687], name: "Dōtonbori" },
  // Seoul
  { coord: [126.9831, 37.5826], name: "Bukchon Hanok Village" },
  { coord: [126.9869, 37.5759], name: "Insadong" },
  { coord: [126.9244, 37.5563], name: "Hongdae" },
  // Hong Kong
  { coord: [114.1500, 22.2810], name: "Soho (Hollywood Rd)" },
  { coord: [114.1717, 22.2783], name: "Star Ferry promenade (TST)" },
  // Singapore
  { coord: [103.8434, 1.2839], name: "Marina Bay waterfront" },
  { coord: [103.8488, 1.3138], name: "Tiong Bahru (Yong Siak St)" },
  { coord: [103.8500, 1.3082], name: "Kampong Glam (Haji Lane)" },
  // Bangkok
  { coord: [100.4988, 13.7438], name: "Old City (Phra Athit)" },
  // Hanoi
  { coord: [105.8511, 21.0331], name: "Old Quarter (Hàng Gai)" },
  // Ho Chi Minh City
  { coord: [106.6996, 10.7769], name: "District 1 (Đồng Khởi)" },
  // Mumbai
  { coord: [72.8311, 18.9265], name: "Colaba Causeway" },
  // Delhi
  { coord: [77.2310, 28.6139], name: "Connaught Place" },
  { coord: [77.2300, 28.6562], name: "Chandni Chowk" },
  // Sydney
  { coord: [151.2093, -33.8688], name: "The Rocks" },
  { coord: [151.2200, -33.8568], name: "Sydney Harbour foreshore (Mrs Macquarie's)" },
  { coord: [151.2772, -33.8915], name: "Paddington (Oxford St)" },
  // Melbourne
  { coord: [144.9631, -37.8136], name: "Laneways (Hosier Lane)" },
  { coord: [144.9784, -37.8003], name: "Fitzroy (Brunswick St)" },
  // Auckland
  { coord: [174.7633, -36.8485], name: "Viaduct Harbour" },
  // Buenos Aires
  { coord: [-58.4222, -34.5878], name: "Palermo Soho" },
  { coord: [-58.3633, -34.6178], name: "San Telmo (Defensa)" },
  { coord: [-58.3880, -34.5870], name: "Recoleta" },
  // Rio de Janeiro
  { coord: [-43.1729, -22.9068], name: "Santa Teresa" },
  { coord: [-43.1818, -22.9711], name: "Ipanema beachfront" },
  // Cartagena
  { coord: [-75.5512, 10.4236], name: "Walled City (Calle del Estanco)" },
  // Lima
  { coord: [-77.0282, -12.1196], name: "Barranco (Bajada de los Baños)" },
  // Havana
  { coord: [-82.3553, 23.1402], name: "Old Havana (Calle Obispo)" },
];


/**
 * Curated catalog of QUIRKY / story-worthy walking landmarks — pop-culture
 * spots, famous addresses, weird historical markers. These are the things
 * a friend who knew the city would point out as you walked past. They get
 * an even higher score than scenic streets so the walking router will
 * actively reach for them when they sit near the corridor.
 */
/**
 * Curated catalog of HISTORIC walking landmarks — Freedom Trail stops,
 * famous old cemeteries, signers' graves, founding-era churches and
 * meeting houses. These are the "you're walking past actual American
 * history" pins. Google Places lists most of these but they're easy to
 * miss in a crowded "tourist_attraction" feed; surfacing them as a
 * dedicated curated category lets the user say "more like this" and get
 * a self-guided history walk.
 */
export const HISTORIC_WALKING_LANDMARKS: BeautifulWalkingStreet[] = [
  // Boston — Freedom Trail (in walking order)
  { coord: [-71.0648, 42.3554], name: "Boston Common (Freedom Trail start)" },
  { coord: [-71.0633, 42.3578], name: "Massachusetts State House" },
  { coord: [-71.0619, 42.3570], name: "Park Street Church" },
  { coord: [-71.0617, 42.3566], name: "Granary Burying Ground (Paul Revere, Sam Adams)" },
  { coord: [-71.0606, 42.3576], name: "King's Chapel & Burying Ground" },
  { coord: [-71.0584, 42.3585], name: "Old Corner Bookstore" },
  { coord: [-71.0578, 42.3582], name: "Old South Meeting House (Boston Tea Party)" },
  { coord: [-71.0576, 42.3589], name: "Old State House (Boston Massacre site)" },
  { coord: [-71.0568, 42.3601], name: "Faneuil Hall" },
  { coord: [-71.0537, 42.3636], name: "Paul Revere House" },
  { coord: [-71.0544, 42.3664], name: "Old North Church (\"one if by land\")" },
  { coord: [-71.0566, 42.3675], name: "Copp's Hill Burying Ground" },
  { coord: [-71.0606, 42.3764], name: "USS Constitution (Old Ironsides)" },
  { coord: [-71.0606, 42.3766], name: "Bunker Hill Monument" },
  // New York City — historic
  { coord: [-74.0134, 40.7081], name: "Trinity Church & graveyard (Hamilton's grave)" },
  { coord: [-74.0093, 40.7115], name: "St. Paul's Chapel (1766, where Washington prayed)" },
  { coord: [-74.0125, 40.7068], name: "Federal Hall (Washington's inauguration)" },
  { coord: [-73.9442, 40.8201], name: "Woodlawn Cemetery (Miles Davis, Duke Ellington)" },
  { coord: [-73.9881, 40.6674], name: "Green-Wood Cemetery (Brooklyn)" },
  { coord: [-73.9907, 40.7338], name: "New York Marble Cemetery" },
  // Philadelphia
  { coord: [-75.1503, 39.9489], name: "Independence Hall" },
  { coord: [-75.1503, 39.9496], name: "Liberty Bell" },
  { coord: [-75.1437, 39.9540], name: "Christ Church Burial Ground (Ben Franklin)" },
  { coord: [-75.1448, 39.9523], name: "Elfreth's Alley (oldest continuously-inhabited street)" },
  // Washington DC
  { coord: [-77.0353, 38.8895], name: "Lincoln Memorial" },
  { coord: [-77.0501, 38.8783], name: "Arlington National Cemetery" },
  // San Francisco
  { coord: [-122.4090, 37.8060], name: "Mission Dolores (1776, oldest SF building)" },
  // Paris
  { coord: [2.3931, 48.8606], name: "Père Lachaise Cemetery (Jim Morrison, Oscar Wilde)" },
  { coord: [2.3433, 48.8462], name: "Catacombs of Paris" },
  // London
  { coord: [-0.1276, 51.4994], name: "Westminster Abbey (royal tombs)" },
  { coord: [-0.1500, 51.5670], name: "Highgate Cemetery (Karl Marx)" },
  { coord: [-0.0759, 51.5081], name: "Tower of London" },
];

/**
 * Curated catalog of FAMOUS / iconic city bakeries — the lines-around-the-block
 * places people make pilgrimages to. Google's "bakery" type returns hundreds
 * per city; this list is the must-stop ones a local would recommend.
 */
export const FAMOUS_BAKERIES: BeautifulWalkingStreet[] = [
  // New York City
  { coord: [-73.9971, 40.7263], name: "Levain Bakery (cookies)" },
  { coord: [-73.9559, 40.7794], name: "Levain Bakery — UWS" },
  { coord: [-74.0058, 40.7368], name: "Magnolia Bakery (Bleecker St)" },
  { coord: [-73.9583, 40.7185], name: "Peter Pan Donuts (Greenpoint)" },
  { coord: [-73.9430, 40.7193], name: "L'Industrie Pizzeria (Williamsburg)" },
  { coord: [-73.9890, 40.7196], name: "Russ & Daughters" },
  { coord: [-73.9904, 40.7218], name: "Yonah Schimmel Knish Bakery" },
  { coord: [-73.9870, 40.7177], name: "Doughnut Plant" },
  // San Francisco
  { coord: [-122.4197, 37.7596], name: "Tartine Bakery (Mission)" },
  { coord: [-122.4090, 37.8060], name: "Boudin Bakery (sourdough, Wharf)" },
  { coord: [-122.4480, 37.7740], name: "Arsicault Bakery (best croissant in America)" },
  // Boston
  { coord: [-71.0563, 42.3650], name: "Mike's Pastry (cannoli, North End)" },
  { coord: [-71.0566, 42.3651], name: "Modern Pastry (cannoli rival)" },
  { coord: [-71.0565, 42.3641], name: "Bova's Bakery (24/7 North End)" },
  // Paris
  { coord: [2.3267, 48.8550], name: "Poilâne (Saint-Germain)" },
  { coord: [2.3400, 48.8556], name: "Du Pain et des Idées" },
  { coord: [2.3640, 48.8716], name: "Boulangerie Utopie" },
  // London
  { coord: [-0.1066, 51.5230], name: "St. JOHN Bakery" },
  { coord: [-0.1234, 51.5145], name: "Maison Bertaux (Soho)" },
];

export const QUIRKY_WALKING_LANDMARKS: BeautifulWalkingStreet[] = [
  // New York City
  { coord: [-73.9762, 40.7757], name: "The Plaza Hotel" },
  { coord: [-73.9762, 40.7757], name: "FAO Schwarz (Bergdorf corner)" },
  { coord: [-73.9762, 40.7686], name: "The Dakota — where John Lennon was shot" },
  { coord: [-73.9758, 40.7759], name: "Strawberry Fields (Central Park)" },
  { coord: [-74.0026, 40.7320], name: "Friends apartment (90 Bedford St)" },
  { coord: [-74.0030, 40.7318], name: "Carrie Bradshaw's stoop (66 Perry St)" },
  { coord: [-73.9899, 40.7484], name: "Empire State Building" },
  { coord: [-73.9851, 40.7589], name: "Times Square" },
  { coord: [-73.9787, 40.7527], name: "Grand Central Terminal" },
  { coord: [-73.9776, 40.7527], name: "Chrysler Building" },
  { coord: [-73.9663, 40.7720], name: "The Met (Met Steps from Gossip Girl)" },
  { coord: [-73.9554, 40.7782], name: "Guggenheim Museum" },
  { coord: [-73.9737, 40.7639], name: "Tiffany & Co. flagship" },
  { coord: [-73.9638, 40.7791], name: "Frick Collection (old Vanderbilt-era mansion)" },
  { coord: [-73.9629, 40.7747], name: "Carnegie Mansion (Cooper Hewitt)" },
  { coord: [-73.9583, 40.7794], name: "Gracie Mansion" },
  { coord: [-73.9665, 40.7795], name: "1040 Fifth Ave (Jackie Kennedy's home)" },
  { coord: [-73.9836, 40.7484], name: "Flatiron Building" },
  { coord: [-73.9967, 40.7223], name: "Katz's Delicatessen (When Harry Met Sally)" },
  { coord: [-74.0445, 40.6892], name: "Statue of Liberty viewpoint (Battery Park)" },
  { coord: [-74.0134, 40.7127], name: "9/11 Memorial" },
  { coord: [-73.9905, 40.7359], name: "Stonewall Inn" },
  { coord: [-73.9990, 40.7503], name: "Ghostbusters firehouse (Hook & Ladder 8)" },
  // San Francisco
  { coord: [-122.4192, 37.8021], name: "Lombard Street (the crooked one)" },
  { coord: [-122.4477, 37.8025], name: "Painted Ladies (Full House row)" },
  { coord: [-122.4090, 37.8060], name: "Pier 39 sea lions" },
  // Boston
  { coord: [-71.0566, 42.3596], name: "Cheers bar (Beacon St)" },
  { coord: [-71.0565, 42.3589], name: "Make Way for Ducklings statues" },
  // Chicago
  { coord: [-87.6234, 41.8826], name: "The Bean (Cloud Gate)" },
  // Paris
  { coord: [2.3522, 48.8566], name: "Notre-Dame de Paris" },
  { coord: [2.2945, 48.8584], name: "Eiffel Tower" },
  { coord: [2.3376, 48.8606], name: "Louvre courtyard" },
  { coord: [2.3324, 48.8867], name: "Sacré-Cœur (Montmartre)" },
  { coord: [2.3499, 48.8530], name: "Shakespeare and Company bookshop" },
  // London
  { coord: [-0.1246, 51.5007], name: "Big Ben / Westminster" },
  { coord: [-0.1410, 51.5014], name: "Buckingham Palace gates" },
  { coord: [-0.1581, 51.5237], name: "Abbey Road crossing (Beatles)" },
  { coord: [-0.1244, 51.5237], name: "Platform 9¾ (King's Cross)" },
  { coord: [-0.1585, 51.5238], name: "221B Baker Street (Sherlock)" },
];

/** Curated beautiful-walking-street candidates within the corridor. Walking only. */
function beautifulWalkingStreetCandidates(
  start: LngLat,
  end: LngLat,
  mode: TravelMode | undefined,
  maxDetourKm: number,
): Candidate[] {
  if (mode !== "walking") return [];
  const out: Candidate[] = [];
  for (const s of BEAUTIFUL_WALKING_STREETS) {
    const { devKm, t: alongT } = projectOntoSegment(start, end, s.coord);
    if (devKm > maxDetourKm) continue;
    if (alongT < 0.05 || alongT > 0.95) continue;
    const closeness = Math.pow(1 - devKm / (maxDetourKm + 0.001), 2);
    const score = 1.6 + 0.2 * closeness;
    out.push({ coord: s.coord, kind: "landmark", name: s.name, score, alongT });
  }
  // Quirky / story-worthy landmarks: scored higher so the walking router
  // actively reaches for them when they sit near the corridor — these are
  // the "the place where Lennon got shot" moments that turn a walk into
  // a tour.
  for (const s of QUIRKY_WALKING_LANDMARKS) {
    const { devKm, t: alongT } = projectOntoSegment(start, end, s.coord);
    if (devKm > maxDetourKm) continue;
    if (alongT < 0.05 || alongT > 0.95) continue;
    const closeness = Math.pow(1 - devKm / (maxDetourKm + 0.001), 2);
    const score = 1.9 + 0.25 * closeness;
    out.push({ coord: s.coord, kind: "landmark", name: s.name, score, alongT });
  }
  return out;
}

/**
 * Some local corridors have a known "right answer" backroad sequence that
 * Mapbox would never find on its own (because each leg is slightly slower
 * than the arterial). For those corridors we return an ordered sequence of
 * waypoints AND a label, so the caller can issue a single multi-waypoint
 * Mapbox request that's actually constrained onto the right streets.
 */
export interface LocalBackroadSequence {
  /** Ordered waypoints to insert between start and end, in route order. */
  waypoints: PleasantWaypoint[];
  /** Human-readable label for the route badge / detour panel. */
  label: string;
  /** Reason shown to the user. */
  reason: string;
}

export function localBackroadSequence(
  start: LngLat,
  end: LngLat,
  mode?: TravelMode,
): LocalBackroadSequence | null {
  if (mode !== "driving") return null;

  // Tarrytown ↔ Sleepy Hollow / Briarcliff Manor / Pocantico Hills:
  // Highland → Sunnyside → Neperan by the Tarrytown Lakes reservoir,
  // instead of South/North Broadway (US 9). The bbox covers north up
  // through Briarcliff Manor (~41.14) because 777 Old Sleepy Hollow Rd
  // Ext sits just north of the original cutoff (41.125) and the
  // reservoir-via-Neperan path is still the obvious pleasant
  // alternative for trips ending there. Real-drive note (#47): the
  // recommended route went through busy downtown instead of along the
  // reservoir because the destination fell outside this bbox by
  // ~1.5 km.
  const tarrytownSleepyHollow = {
    minLng: -73.89,
    maxLng: -73.80,
    minLat: 41.055,
    maxLat: 41.170,
  };
  if (insideBox(start, tarrytownSleepyHollow) && insideBox(end, tarrytownSleepyHollow)) {
    // Direction-aware ordering: the waypoint sequence must go in trip order
    // or Mapbox will zig-zag. Sort by "alongT" against the start→end line.
    const raw: PleasantWaypoint[] = [
      { coord: [-73.8556, 41.0739], kind: "park",  name: "Highland Avenue" },
      { coord: [-73.8527, 41.0795], kind: "park",  name: "Sunnyside Avenue" },
      { coord: [-73.8453, 41.0823], kind: "water", name: "Tarrytown Lakes reservoir (Neperan Rd)" },
    ];
    // Keep the real drivable sequence. A straight-line projection can reorder
    // these because Neperan sits east of Sunnyside; that accidentally creates
    // Highland → Neperan → Sunnyside, which lets Mapbox bend back toward
    // Broadway. Use south/north endpoint proximity only to reverse the whole
    // sequence for the return trip.
    const forwardCost = distKm(start, raw[0].coord) + distKm(end, raw[raw.length - 1].coord);
    const reverseCost = distKm(start, raw[raw.length - 1].coord) + distKm(end, raw[0].coord);
    const ordered = reverseCost < forwardCost ? raw.slice().reverse() : raw;
    return {
      waypoints: ordered,
      label: "Tarrytown Lakes reservoir via Neperan Road",
      reason: "Backroad route via Highland Avenue, Sunnyside Avenue, and Neperan Road by the reservoir",
    };
  }

  // ROUTE 1 PREFERENCE
  // Generic constraint: if both endpoints sit reasonably close to the
  // Pacific Coast Highway corridor and the trip runs roughly along it,
  // force the routing engine through a few CA-1 anchor points so the
  // recommended pleasant route stays on the coast instead of cutting
  // inland (e.g. SF→HMB defaulting to I-280, or LA→SB taking US-101).
  //
  // Anchors are an ordered north→south polyline of points that sit
  // *on* CA-1 mid-segment, so Mapbox/Valhalla snap onto the coastal
  // road. Add freely — extra anchors just give finer-grained matching.
  const route1Match = matchCoastalCorridor(start, end, ROUTE_1_ANCHORS, {
    label: "Pacific Coast Highway (CA-1)",
    kind: "water",
    reasonTemplate: "Coastal route along CA-1",
  });
  if (route1Match) return route1Match;

  // LAKE MICHIGAN SHORELINE (Morgan, Aug 1: "we really want to emphasize
  // going by lake michigan whenever possible").
  //
  // Prompted by a West Michigan reviewer: Holland → Traverse City, water
  // view selected, and the app still ran him inland through Grand Rapids —
  // "pretty much NO water view." He was right, and the curated data was not
  // the problem: every road he named (Lakeshore Drive, Old US-31, M-116,
  // M-22 through Frankfort and Empire) is already in greatDrives/MI.json,
  // correctly scored. Nothing was reaching for them.
  //
  // The reason the generic corridor search doesn't find this coast: it BOWS.
  // Ludington sits ~50km west of the straight Holland→Traverse City line and
  // Frankfort ~45km, so the default 25km `anchorMaxKm` rejects the entire
  // northern half of the shoreline and keeps only Grand Haven and Muskegon —
  // a route that starts on the water and then gives up. Hence anchorMaxKm 70:
  // the lake is genuinely that far off the direct line, and driving to it is
  // the whole point.
  const lakeMichiganMatch = matchCoastalCorridor(start, end, LAKE_MICHIGAN_ANCHORS, {
    label: "Lake Michigan shoreline",
    kind: "water",
    reasonTemplate: "Along the Lake Michigan shore",
  }, {
    anchorMaxKm: 70,
    // 5, not the default 3: with only first/middle/last the router happily
    // cuts inland across the long gaps between them.
    maxWaypoints: 5,
    // REACH, and the guard that makes the reach safe (Morgan's call, Aug 1).
    // 60km lets a trip that STARTS inland still be offered the shore —
    // Grand Rapids sits ~47km from the nearest anchor, and Grand Rapids →
    // Traverse City is a drive that should absolutely be able to go by the
    // water. But 60km alone would also catch Kalamazoo → Grand Rapids, a
    // 75km inland hop that has no business being routed out to the lake and
    // back. The length floor separates the two: reaching the coast is worth
    // a detour on a day-long drive and absurd on an hour-long one.
    endpointMaxKm: 60,
    minTripKm: 150,
  });
  if (lakeMichiganMatch) return lakeMichiganMatch;

  return null;
}

/** Anchors on or immediately adjacent to CA-1, ordered north→south.
 *  Each sits mid-segment so the routing engine snaps onto the coastal
 *  highway rather than a parallel inland arterial. */
const ROUTE_1_ANCHORS: { coord: LngLat; name: string }[] = [
  { coord: [-123.8053, 39.4457], name: "Fort Bragg (CA-1)" },
  { coord: [-123.7997, 39.3076], name: "Mendocino headlands (CA-1)" },
  { coord: [-123.6919, 38.9085], name: "Point Arena (CA-1)" },
  { coord: [-123.0480, 38.3330], name: "Bodega Bay (CA-1)" },
  { coord: [-122.6420, 37.9009], name: "Stinson Beach (CA-1)" },
  { coord: [-122.5740, 37.8590], name: "Muir Beach (CA-1)" },
  { coord: [-122.4869, 37.6010], name: "Pacifica (CA-1)" },
  { coord: [-122.5167, 37.5466], name: "Devil's Slide / Montara (CA-1)" },
  { coord: [-122.4280, 37.4636], name: "Half Moon Bay (CA-1)" },
  { coord: [-122.3830, 37.2550], name: "Pescadero coast (CA-1)" },
  { coord: [-122.1970, 37.0150], name: "Davenport (CA-1)" },
  { coord: [-122.0263, 36.9741], name: "Santa Cruz (CA-1)" },
  { coord: [-121.7870, 36.8050], name: "Moss Landing (CA-1)" },
  { coord: [-121.9233, 36.5552], name: "Carmel-by-the-Sea (CA-1)" },
  { coord: [-121.9020, 36.3742], name: "Bixby Bridge (CA-1)" },
  { coord: [-121.8081, 36.2704], name: "Big Sur (CA-1)" },
  { coord: [-121.5380, 36.0140], name: "Lucia coast (CA-1)" },
  { coord: [-121.3270, 35.7770], name: "Ragged Point (CA-1)" },
  { coord: [-121.1840, 35.6440], name: "San Simeon (CA-1)" },
  { coord: [-121.0807, 35.5641], name: "Cambria (CA-1)" },
  { coord: [-120.8500, 35.3658], name: "Morro Bay (CA-1)" },
  { coord: [-120.6413, 35.1428], name: "Pismo Beach (CA-1)" },
  { coord: [-120.2280, 34.4730], name: "Gaviota coast (CA-1/101)" },
  { coord: [-119.6982, 34.4208], name: "Santa Barbara (CA-1/101)" },
  { coord: [-119.2945, 34.2746], name: "Ventura (CA-1/101)" },
  { coord: [-118.7798, 34.0259], name: "Malibu (CA-1)" },
  { coord: [-118.4948, 34.0195], name: "Santa Monica (CA-1)" },
  { coord: [-117.7854, 33.5427], name: "Laguna Beach (CA-1)" },
];

/** Anchors on Michigan's Lake Michigan shore, ordered south→north.
 *
 *  Coordinates are lifted from the already-validated `samples` in
 *  content/greatDrives/MI.json rather than invented — each one sits
 *  mid-segment on the actual shoreline road (Blue Star Highway, Lakeshore
 *  Drive, Scenic Drive, Old US-31, M-116, M-22), so the router snaps onto
 *  the coast road instead of the parallel US-31 expressway inland.
 *
 *  This is the drive West Michigan considers the definitive one: Holland →
 *  Grand Haven → Ludington → Manistee → Frankfort → Traverse City. */
const LAKE_MICHIGAN_ANCHORS: { coord: LngLat; name: string }[] = [
  { coord: [-86.7439, 41.7939], name: "New Buffalo" },
  { coord: [-86.2736, 42.4031], name: "South Haven (Blue Star Highway)" },
  { coord: [-86.2010, 42.6550], name: "Saugatuck / Douglas (Blue Star Highway)" },
  { coord: [-86.1700, 42.8800], name: "Holland lakeshore (Lakeshore Drive)" },
  { coord: [-86.2050, 42.9700], name: "Ottawa County dunes (Lakeshore Drive)" },
  { coord: [-86.2284, 43.0631], name: "Grand Haven" },
  { coord: [-86.3300, 43.1900], name: "Lake Harbor / Pere Marquette (Scenic Drive)" },
  { coord: [-86.2484, 43.2342], name: "Muskegon (Lakeshore Drive)" },
  { coord: [-86.4880, 43.6610], name: "Silver Lake / Hart-Mears" },
  { coord: [-86.4320, 43.7817], name: "Pentwater" },
  { coord: [-86.4526, 43.9553], name: "Ludington" },
  { coord: [-86.4900, 44.0100], name: "Ludington State Park (M-116)" },
  { coord: [-86.3240, 44.2450], name: "Manistee (Old US-31 lakeshore)" },
  { coord: [-86.2360, 44.4950], name: "Onekama / Arcadia (M-22)" },
  { coord: [-86.2345, 44.6336], name: "Frankfort (M-22)" },
  { coord: [-86.1800, 44.6900], name: "Empire / Platte Lakeshore (M-22)" },
  { coord: [-86.0580, 44.8114], name: "Glen Arbor / Sleeping Bear (M-22)" },
  { coord: [-85.6206, 44.7631], name: "Traverse City (M-22 Elmwood)" },
];

/**
 * Generic coastal-corridor matcher. Given an ordered anchor polyline,
 * return a waypoint sequence iff:
 *   - both endpoints are within `endpointMaxKm` perpendicular of the line
 *   - the trip alignment with the corridor is meaningful (alongT spread
 *     between endpoints ≥ `minAlongSpan`, i.e. you're going *along* the
 *     coast, not crossing it)
 *   - we can find ≥1 anchor between the endpoints to force the route
 *     onto the highway
 *
 * Picks at most 3 anchors, spread out, so Mapbox's multi-stop routing
 * stays cheap and we don't over-constrain on short hops.
 */
function matchCoastalCorridor(
  start: LngLat,
  end: LngLat,
  anchors: { coord: LngLat; name: string }[],
  opts: { label: string; kind: WaypointKind; reasonTemplate: string },
  config: {
    endpointMaxKm?: number;
    anchorMaxKm?: number;
    minTripKm?: number;
    minAlongSpan?: number;
    maxWaypoints?: number;
  } = {},
): LocalBackroadSequence | null {
  const endpointMaxKm = config.endpointMaxKm ?? 35;
  const anchorMaxKm   = config.anchorMaxKm   ?? 25;
  const minTripKm     = config.minTripKm     ?? 8;
  const minAlongSpan  = config.minAlongSpan  ?? 0.05;
  const maxWaypoints  = config.maxWaypoints  ?? 3;

  if (distKm(start, end) < minTripKm) return null;

  // Project each anchor onto the start→end line.
  const projected = anchors.map((a) => {
    const { devKm, t } = projectOntoSegment(start, end, a.coord);
    return { ...a, devKm, t };
  });

  // Endpoints must be near *some* anchor — i.e. both ends actually live
  // on the coast, not just one side. Cheaper proxy: nearest-anchor dist.
  const nearestKm = (p: LngLat) =>
    Math.min(...anchors.map((a) => distKm(p, a.coord)));
  if (nearestKm(start) > endpointMaxKm) return null;
  if (nearestKm(end)   > endpointMaxKm) return null;

  // Anchors that sit between start and end and don't pull us far off-line.
  const between = projected
    .filter((p) => p.t > 0.02 && p.t < 0.98 && p.devKm <= anchorMaxKm)
    .sort((a, b) => a.t - b.t);
  if (between.length === 0) return null;

  const tSpan = between[between.length - 1].t - between[0].t;
  if (tSpan < minAlongSpan) return null;

  // Spread-pick: take first, last, and one in the middle (if 3+ available).
  const picks: typeof between = [];
  if (between.length <= maxWaypoints) {
    picks.push(...between);
  } else {
    const idxs = Array.from({ length: maxWaypoints }, (_, i) =>
      Math.round((i * (between.length - 1)) / (maxWaypoints - 1)),
    );
    for (const i of idxs) picks.push(between[i]);
  }

  const waypoints: PleasantWaypoint[] = picks.map((p) => ({
    coord: p.coord,
    kind: opts.kind,
    name: p.name,
  }));

  const first = picks[0].name.replace(/\s*\([^)]*\)\s*$/, "");
  const last  = picks[picks.length - 1].name.replace(/\s*\([^)]*\)\s*$/, "");
  const reason = picks.length === 1
    ? `${opts.reasonTemplate} via ${first}`
    : `${opts.reasonTemplate} via ${first} → ${last}`;

  return {
    waypoints,
    label: opts.label,
    reason,
  };
}

/** Resolve which feature kinds to fetch, blending explicit prefs with the
 *  user's chosen pleasantness profile. The profile guarantees we always
 *  have *something* to detour through, even when the user has no loves
 *  selected. */
function wantsFromInputs(
  prefs: Preference[],
  profile: PleasantnessProfile | undefined,
  mode: TravelMode | undefined,
) {
  let wantsWater = prefs.includes("scenic");
  let wantsPark =
    prefs.includes("parks") ||
    prefs.includes("scenic") ||
    prefs.includes("quiet");
  let wantsHistoric =
    prefs.includes("historic") ||
    prefs.includes("monuments") ||
    prefs.includes("architecture");

  // Profile backstop: even without explicit loves, a "Water views" profile
  // user expects a waterfront detour candidate.
  if (profile === "water") wantsWater = true;
  if (profile === "nature") wantsPark = true;
  if (profile === "historic") {
    wantsHistoric = true;
  }
  // "lively" and "quiet" profiles get parks too — they're calm + interesting.
  if (profile === "lively") wantsPark = true;
  if (profile === "quiet") wantsPark = true;

  // PLEASANT-ROUTE BASELINE: for driving + biking, always look for parks
  // and water — that's the whole point of "pleasant". For WALKING in
  // dense cities, forcing a waterfront detour produces absurd results
  // (e.g. a Tompkins Sq → Central Park walk looping out to the East
  // River). Walking only gets the water/park baseline when the user
  // explicitly asked for it via prefs or chose a matching profile.
  if (mode !== "walking") {
    wantsWater = true;
    wantsPark = true;
  }

  // Walking ALWAYS considers landmarks — strolling a city is about sights.
  // Lively profile cares about landmarks regardless of mode.
  const wantsLandmarks =
    mode === "walking" || wantsHistoric || profile === "lively";

  return { wantsWater, wantsPark, wantsHistoric, wantsLandmarks };
}

function buildOverpassQuery(
  bb: string,
  wants: ReturnType<typeof wantsFromInputs>,
): string | null {
  const blocks: string[] = [];
  if (wants.wantsWater) {
    blocks.push(`way["natural"="coastline"](${bb});`);
    blocks.push(`way["natural"="water"]["name"](${bb});`);
    blocks.push(`relation["natural"="water"]["name"](${bb});`);
    blocks.push(`way["landuse"="reservoir"]["name"](${bb});`);
    blocks.push(`relation["landuse"="reservoir"]["name"](${bb});`);
  }
  if (wants.wantsPark) {
    blocks.push(`way["leisure"="park"]["name"](${bb});`);
    blocks.push(`way["boundary"="national_park"](${bb});`);
    blocks.push(`way["leisure"="garden"]["name"](${bb});`);
    // STATE / regional parks + nature preserves — huge, mostly missed
    // by leisure=park alone. Real-drive (#52): "the recommended route
    // should consider state parks and nature preserves the same way
    // it considers city parks." Most US state parks tag as
    // boundary=protected_area; nature reserves use leisure=nature_reserve.
    blocks.push(`way["boundary"="protected_area"]["name"](${bb});`);
    blocks.push(`relation["boundary"="protected_area"]["name"](${bb});`);
    blocks.push(`way["leisure"="nature_reserve"]["name"](${bb});`);
    blocks.push(`relation["leisure"="nature_reserve"]["name"](${bb});`);
    // Named forests — large green canopies that often run along
    // scenic drives even when they aren't "parks" per se.
    blocks.push(`way["natural"="wood"]["name"](${bb});`);
  }
  if (wants.wantsHistoric) {
    blocks.push(`node["historic"](${bb});`);
    blocks.push(`way["historic"]["name"](${bb});`);
  }
  if (wants.wantsLandmarks) {
    blocks.push(
      `node["tourism"~"^(attraction|museum|viewpoint|gallery|artwork)$"]["name"](${bb});`,
    );
    blocks.push(
      `way["tourism"~"^(attraction|museum|viewpoint|gallery)$"]["name"](${bb});`,
    );
    blocks.push(
      `node["historic"~"^(monument|memorial|castle|fort|ruins|tower)$"]["name"](${bb});`,
    );
    blocks.push(
      `way["historic"~"^(monument|memorial|castle|fort|ruins|tower)$"]["name"](${bb});`,
    );
  }
  if (blocks.length === 0) return null;
  return `[out:json][timeout:20];(${blocks.join("")});out center 200;`;
}

// Exported for unit tests. Pure function — given OSM tags, returns the
// scenic-anchor kind the route system should treat them as.
export function classifyTags(t: Record<string, string>): WaypointKind | null {
  if (
    t.natural === "coastline" ||
    t.natural === "water" ||
    t.waterway ||
    t.landuse === "reservoir"
  )
    return "water";
  if (
    t.leisure === "park" ||
    t.boundary === "national_park" ||
    t.boundary === "protected_area" ||
    t.leisure === "nature_reserve" ||
    t.leisure === "garden" ||
    t.natural === "wood"
  )
    return "park";
  if (
    t.tourism === "attraction" ||
    t.tourism === "museum" ||
    t.tourism === "viewpoint" ||
    t.tourism === "gallery" ||
    t.tourism === "artwork"
  )
    return "landmark";
  if (
    t.historic === "monument" ||
    t.historic === "memorial" ||
    t.historic === "castle" ||
    t.historic === "fort" ||
    t.historic === "ruins" ||
    t.historic === "tower"
  )
    return "landmark";
  if (t.historic) return "historic";
  return null;
}

function kindWeight(
  k: WaypointKind,
  wants: ReturnType<typeof wantsFromInputs>,
  profile: PleasantnessProfile | undefined,
  mode: TravelMode | undefined,
): number {
  // Profile is the strongest signal — boost the matching kind.
  if (profile === "water" && k === "water") return 1.5;
  if (profile === "nature" && k === "park") return 1.4;
  if (profile === "historic" && (k === "historic" || k === "landmark"))
    return 1.4;
  if (profile === "lively" && k === "landmark") return 1.3;
  if (profile === "quiet" && k === "park") return 1.2;

  if (mode === "walking") {
    if (k === "landmark") return 1.3;
    if (k === "water" && wants.wantsWater) return 1.2;
    if (k === "park" && wants.wantsPark) return 1.1;
    if (k === "historic") return 1.0;
  } else {
    if (k === "water" && wants.wantsWater) return 1.2;
    if (k === "park" && wants.wantsPark) return 1.1;
    if (k === "landmark") return wants.wantsLandmarks ? 1.05 : 0.7;
    if (k === "historic" && wants.wantsHistoric) return 1.0;
  }
  return 0.6;
}

/**
 * Find the SINGLE best pleasant waypoint. Kept for backward compatibility
 * with callers that only want one detour.
 */
export async function findPleasantWaypoint(
  start: LngLat,
  end: LngLat,
  prefs: Preference[],
  maxDetourKm: number,
  mode?: TravelMode,
  profile?: PleasantnessProfile,
): Promise<PleasantWaypoint | undefined> {
  const list = await findPleasantWaypoints(
    start,
    end,
    prefs,
    maxDetourKm,
    mode,
    profile,
    1,
  );
  return list[0];
}

/**
 * Find up to `topN` pleasant waypoints. The returned list is spread out
 * along the trip (no two waypoints inside the same trip-third) so each one
 * gives Mapbox a meaningfully different route to compute.
 */
/** Pure-function clusterer for community tags. Exposed for unit tests.
 *
 *  Bucket each positive (sentiment "positive" AND pleasantness ≥ 2) tag into a
 *  ~50m grid cell. Cells with ≥ COMMUNITY_ANCHOR_MIN_TAGS distinct tags
 *  become anchor candidates whose coord is the average of the cell's
 *  contained points. Score reflects cluster size (more votes = stronger).
 *
 *  This is the "community-as-anchor" wire-up (#98): instead of community
 *  ratings only nudging the scoring of routes that happen to pass nearby,
 *  high-confidence clusters actively pull routes through them. */
export function clusterCommunityAnchors(tags: CommunityTag[]): Array<{
  coord: LngLat;
  voteCount: number;
  avgPleasantness: number;
  sampleLabel?: string;
}> {
  const cells = new Map<string, CommunityTag[]>();
  for (const t of tags) {
    if (t.sentiment !== "positive") continue;
    if (typeof t.pleasantness === "number" && t.pleasantness < 2) continue;
    // Math.floor (not Math.round) so a 50m grid cell has predictable
    // boundaries — Math.round rounds .5 up and can split tags that are
    // ~25m apart but near a cell boundary into different buckets.
    const cellLat = Math.floor(t.lat / COMMUNITY_ANCHOR_GRID_DEG);
    const cellLng = Math.floor(t.lng / COMMUNITY_ANCHOR_GRID_DEG);
    const key = `${cellLat}|${cellLng}`;
    const arr = cells.get(key) ?? [];
    arr.push(t);
    cells.set(key, arr);
  }
  const out: Array<{
    coord: LngLat;
    voteCount: number;
    avgPleasantness: number;
    sampleLabel?: string;
  }> = [];
  for (const arr of cells.values()) {
    if (arr.length < COMMUNITY_ANCHOR_MIN_TAGS) continue;
    let sumLng = 0;
    let sumLat = 0;
    let sumPleasant = 0;
    let pleasantCount = 0;
    const labels = new Set<string>();
    for (const t of arr) {
      sumLng += t.lng;
      sumLat += t.lat;
      if (typeof t.pleasantness === "number") {
        sumPleasant += t.pleasantness;
        pleasantCount++;
      }
      if (t.tag_label) labels.add(t.tag_label);
    }
    out.push({
      coord: [sumLng / arr.length, sumLat / arr.length],
      voteCount: arr.length,
      avgPleasantness: pleasantCount > 0 ? sumPleasant / pleasantCount : 2,
      // Show the most common label as the anchor's "name" (best-effort).
      sampleLabel: [...labels][0],
    });
  }
  return out;
}

export async function findPleasantWaypoints(
  start: LngLat,
  end: LngLat,
  prefs: Preference[],
  maxDetourKm: number,
  mode?: TravelMode,
  profile?: PleasantnessProfile,
  topN: number = 3,
  /** "Try a different route" support: skip the first N best-scored picks
   *  so the user gets a meaningfully different waypoint each retry. */
  seedOffset: number = 0,
  /** Reject any candidate within ~250m of these coords. Used to guarantee
   *  retries don't re-pick the same physical place. */
  excludeCoords: LngLat[] = [],
): Promise<PleasantWaypoint[]> {
  const localWaypoints = localBackroadWaypoints(start, end, mode);
  const wants = wantsFromInputs(prefs, profile, mode);
  if (
    !wants.wantsWater &&
    !wants.wantsPark &&
    !wants.wantsHistoric &&
    !wants.wantsLandmarks
  ) {
    return localWaypoints.slice(0, topN);
  }

  const tripKm = distKm(start, end);
  const padDeg = Math.min(0.5, Math.max(0.05, tripKm / 400));
  const b = corridorBbox(start, end, padDeg);
  const bb = `${b.minLat},${b.minLng},${b.maxLat},${b.maxLng}`;

  const query = buildOverpassQuery(bb, wants);
  if (!query) return localWaypoints.slice(0, topN);

  // Kick off Overpass (OSM scenic anchors) AND community-tag fetch in
  // parallel. Community-tag fetch failure is non-fatal — we just lose the
  // community anchors and proceed with OSM-only candidates.
  // Route through the shared mirror-rotating, cached Overpass helper instead of
  // a naked single-endpoint fetch to the public server (which 429s / CORS-fails
  // and silently returned no pleasant anchors — degrading the Pleasant route).
  const overpassFetch = overpassDirect(query).catch(() => [] as any[]);
  const communityFetch = fetchTagsInBbox({
    minLat: b.minLat, maxLat: b.maxLat,
    minLng: b.minLng, maxLng: b.maxLng,
  }).catch(() => [] as CommunityTag[]);
  const [elements, communityTags] = await Promise.all([overpassFetch, communityFetch]);
  if (!elements) return [];

  // Score each candidate.
  const scored: Candidate[] = [];
  for (const el of elements) {
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    if (typeof lat !== "number" || typeof lon !== "number") continue;
    const t = (el.tags ?? {}) as Record<string, string>;
    const kind = classifyTags(t);
    if (!kind) continue;
    // Landmarks without names are noise (random benches tagged historic).
    if (kind === "landmark" && !t.name) continue;

    // WALKING: skip area-polygon candidates (water + parks). Overpass returns
    // the centroid of the polygon, which usually sits off any walkable road
    // (middle of a lake, deep inside a cemetery or preserve). Mapbox then
    // snaps to the nearest pedestrian edge — often a dead-end driveway —
    // and has to backtrack to reach it, producing a visible U-turn spike
    // in the route. Walks already get scenery from the curated landmark/
    // beautiful-street catalog, which uses on-road point coordinates.
    if (mode === "walking" && (kind === "water" || kind === "park")) continue;

    const coord: LngLat = [lon, lat];
    const { devKm, t: alongT } = projectOntoSegment(start, end, coord);

    // Quality-aware detour cap (walking): scale how far off-route we'll go
    // by the journey length AND the candidate's intrinsic quality. A short
    // walk only includes things very close to the line; if a stop is truly
    // great (famous named landmark, high kind weight), allow it a bit
    // further. Long walks naturally allow more drift.
    let effectiveCap = maxDetourKm;
    if (mode === "walking") {
      // Base cap = ~12% of trip length, floor 250m, ceiling = full maxDetourKm.
      const tripBase = Math.min(maxDetourKm, Math.max(0.25, tripKm * 0.12));
      const kw = kindWeight(kind, wants, profile, mode);
      // Quality multiplier: 1.0 for ordinary picks, up to ~2.2 for the
      // best-named landmarks. Lets "great stuff a little further" through
      // while keeping mediocre stops on a tight leash.
      const named = t.name ? 1 : 0.6;
      const quality = Math.max(0.6, Math.min(2.2, kw * named * 1.4));
      effectiveCap = Math.min(maxDetourKm, tripBase * quality);
    }
    if (devKm > effectiveCap) continue;
    // Skip candidates basically AT the start or end — a "detour" 50m from
    // your destination doesn't change the route.
    if (alongT < 0.08 || alongT > 0.92) continue;

    const closeness = Math.pow(1 - devKm / (effectiveCap + 0.001), 2);
    // Reward candidates near the middle of the trip — they reshape the
    // route most. Punish candidates very close to start/end softly.
    const centrality = 1 - Math.abs(alongT - 0.5) * 1.4;
    const namedBump = t.name ? 0.08 : 0;
    const score =
      kindWeight(kind, wants, profile, mode) *
        (0.7 * closeness + 0.3 * Math.max(0, centrality)) +
      namedBump;


    scored.push({ coord, kind, name: t.name, score, alongT });
  }
  // Inject curated beautiful walking streets (NYC, Paris, etc.) — high-
  // signal picks Overpass alone won't surface as routable points.
  scored.push(...beautifulWalkingStreetCandidates(start, end, mode, maxDetourKm));

  // Community-as-anchor (#98): spots where ≥3 users left positive
  // ratings (≥2 stars) within ~50m become candidate anchors. Score
  // scales with vote count and avg pleasantness, calibrated to land
  // between an ordinary park (kindWeight ~0.7) and a named landmark
  // (~0.9) when 3 users agree, ramping up with more votes.
  const communityClusters = clusterCommunityAnchors(communityTags);
  for (const cluster of communityClusters) {
    const { devKm, t: alongT } = projectOntoSegment(start, end, cluster.coord);
    if (devKm > maxDetourKm) continue;
    if (alongT < 0.08 || alongT > 0.92) continue;
    // Community anchors borrow the standard closeness × centrality scoring,
    // with a vote-count multiplier on top. 3 votes ≈ baseline; 5+ votes
    // start outscoring most OSM anchors.
    const closeness = Math.pow(1 - devKm / (maxDetourKm + 0.001), 2);
    const centrality = 1 - Math.abs(alongT - 0.5) * 1.4;
    const voteMult = Math.min(1.6, 0.6 + cluster.voteCount * 0.18);
    const pleasantMult = Math.max(0.7, cluster.avgPleasantness / 3); // 2★ → 0.67, 3★ → 1.0
    const score =
      voteMult * pleasantMult *
        (0.7 * closeness + 0.3 * Math.max(0, centrality)) +
      0.08; // small named-bump equivalent
    scored.push({
      coord: cluster.coord,
      kind: "community",
      name: cluster.sampleLabel
        ? `Loved by ${cluster.voteCount} locals (${cluster.sampleLabel})`
        : `Loved by ${cluster.voteCount} locals`,
      score,
      alongT,
    });
  }

  if (scored.length === 0) return localWaypoints.slice(0, topN);

  // CURATED CORRIDOR ANCHORS OUTRANK EVERYTHING (Morgan, Aug 1).
  //
  // These arrive from localBackroadSequence — the Lake Michigan shore, CA-1,
  // the Tarrytown reservoir. They only exist when a hand-built corridor
  // MATCHED the trip, which is already a conservative test, so when one fires
  // it is the most confident signal we have about where this drive should go.
  //
  // They were being pushed with NO score. The sort below reads `score ?? 0`,
  // so every one of them landed at the bottom of the list behind any pond
  // Overpass happened to return, and the top-N diversity pass then cut them
  // entirely. Holland → Traverse City generated the full Lake Michigan
  // shoreline sequence and discarded all of it in favour of anchors near
  // Grand Rapids — which is exactly the inland route the West Michigan
  // reviewer complained about. The corridor was never the problem; the
  // ranking was.
  //
  // 3 sits deliberately above both ceilings: OSM anchors top out near 1.1
  // (kindWeight × closeness/centrality + named bump) and community clusters
  // near 2.0 (max vote and pleasantness multipliers).
  const CURATED_CORRIDOR_SCORE = 3;
  scored.push(...localWaypoints.map((wp) => {
    const { t: alongT } = projectOntoSegment(start, end, wp.coord);
    return { ...wp, alongT, score: CURATED_CORRIDOR_SCORE };
  }));

  // CLUSTER BONUS — only for genuinely scenic clusters.
  //
  // Real-drive note (#52): rewarded anchors whose neighborhoods are
  // densely tagged. Real-drive bug (#82): rewarded ALL densely-tagged
  // neighborhoods equally — so downtown Tarrytown (8 cafés + shops +
  // landmarks within 600m) won over the reservoir corridor every time,
  // and the recommended route ran through Main Street instead of along
  // the lake. Restricted: only WATER and PARK anchors count both as
  // bonus recipients AND as cluster contributors. So a riverfront +
  // park district still gets the boost, but a downtown commercial
  // cluster does not — leaving the score to be driven by the actual
  // pleasantness signals (water exposure, residential streets, etc.)
  // for those routes.
  //
  // Additionally subtract a small commercial-density tax from EVERY
  // candidate's score for each commercial neighbor (café/shop/landmark)
  // within the cluster radius — so a candidate anchor surrounded by
  // commerce is gently demoted regardless of its own kind. This catches
  // the case where a single named park sits on the edge of a downtown
  // shopping strip; without the tax, the park anchor still wins and
  // routes go through the strip.
  const CLUSTER_RADIUS_KM = 0.6;
  const CLUSTER_BONUS_CAP = 0.30; // ceiling so density alone can't dominate kind/closeness
  const COMMERCIAL_TAX_PER_NEIGHBOR = 0.04;
  // "community" counts as scenic for cluster purposes — locally-loved
  // spots ARE the scenic signal, regardless of OSM tagging. So a
  // riverfront flagged by 4 community ratings boosts a nearby park
  // anchor the same way a water anchor would.
  const isScenic = (k: WaypointKind) => k === "water" || k === "park" || k === "community";
  const isCommercial = (k: WaypointKind) => k === "landmark";
  // ^ "landmark" is a proxy for commercial-historic density in our taxonomy
  // (cafés / shops aren't in `scored` directly; they appear via Overpass
  // amenity tags that classify as landmark when named). Tune as needed.
  for (const c of scored) {
    let scenicNeighborWeight = 0;
    let commercialNeighborCount = 0;
    for (const other of scored) {
      if (other === c) continue;
      const d = distKm(c.coord, other.coord);
      if (d > CLUSTER_RADIUS_KM) continue;
      if (isScenic(other.kind)) {
        scenicNeighborWeight += (other.score ?? 0) * (1 - d / CLUSTER_RADIUS_KM);
      }
      if (isCommercial(other.kind)) {
        commercialNeighborCount += 1 - d / CLUSTER_RADIUS_KM;
      }
    }
    // Scenic bonus — only applied to scenic anchors. A landmark inside
    // a park cluster doesn't get the boost; the park does.
    if (isScenic(c.kind)) {
      const bonus = Math.min(CLUSTER_BONUS_CAP, scenicNeighborWeight * 0.08);
      c.score = (c.score ?? 0) + bonus;
    }
    // Commercial tax — applied to ALL anchors near commercial density.
    // A scenic anchor on the edge of downtown still gets demoted because
    // routing through it would draw the route into the commercial strip.
    if (commercialNeighborCount > 0) {
      const tax = Math.min(0.25, commercialNeighborCount * COMMERCIAL_TAX_PER_NEIGHBOR);
      c.score = (c.score ?? 0) - tax;
    }
  }

  scored.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  // Diversity pass: pick top candidates but require each new pick to sit in
  // a different along-trip "third" from the already-picked ones AND to be
  // geographically separated by at least 1/3 of the trip distance.
  const minSepKm = Math.max(0.5, tripKm / 3);
  const isExcluded = (c: Candidate) =>
    excludeCoords.some((ex) => distKm(c.coord, ex) < 0.25);
  const eligible = scored.filter((c) => !isExcluded(c));
  // Skip the first `seedOffset` picks (after exclusion) so retries surface
  // genuinely different geometries instead of the same top candidate.
  const pool = eligible.slice(Math.min(seedOffset, Math.max(0, eligible.length - 1)));
  const picked: Candidate[] = [];
  for (const c of pool) {
    if (picked.length >= topN) break;
    let tooClose = false;
    for (const p of picked) {
      const sep = distKm(c.coord, p.coord);
      if (sep < minSepKm) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) continue;
    picked.push(c);
  }

  // Preserve alongT on the returned waypoints so the consumer
  // (generateRoutes) can thread picks in trip order as a sequence
  // route. This is the same trick the hardcoded localBackroadSequence
  // uses, generalized to anywhere we have good anchors. Real-drive
  // note (#51): generic system was producing individual single-
  // waypoint detours when it could have been producing one threaded
  // multi-waypoint route through all of them in order.
  return picked.map((c) => ({ ...c }));
}
